import _ from 'underscore';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { InputControl } from '@bingads-webui/input-control';
import template from './view-template.pug';

export const getBidMultiplierView = ({ i18n, pageContext, multiplierSchema }) => {
  /**
   * Shared view for Hotel Ads Bid Multiplier
   * @extends DeterministicView
   */
  class BidMultiplierView extends DeterministicView {
    initialize(options) {
      const editStrategy = {
        increase: 'increase',
        decrease: 'decrease',
      };

      const strategy = options.strategy || editStrategy.increase;

      this.i18n = i18n;
      this.type = options.type;
      this.value = options.value;
      this.text = options.text;

      this.factorModel = this.initFactorInput('factor-input', strategy);

      this.setState({
        strategy,
        // getState will map '' and undefined to null
        // here just map null to _.noop() since there only have undefined check when setState
        // and also we show keep '0' work for clear situation
        factor: options.factor === null ? _.noop() : options.factor,
      });
    }

    get events() {
      return {
        'change .select-strategy': ({ target }) => {
          this.setState({
            strategy: target.value,
          });
        },
      };
    }

    get template() {
      return template;
    }

    getState() {
      return {
        type: this.type,
        value: this.value,
        isValid: this.getChild('factor-input').getState().isValid,
        factor: this.getFactor(this.model.get('strategy'), this.factorModel.toJSON()),
        percentage: this.factorModel.toJSON() ? this.factorModel.toJSON().Factor : '',
      };
    }

    setState({
      strategy,
      factor,
      isUseParent,
    }) {
      if (!_.isUndefined(isUseParent)) {
        this.model.set({ isUseParent });
        this.getChild('factor-input').setState({ readonly: isUseParent });
      }

      if (strategy && strategy !== this.model.get('strategy')) {
        this.model.set({ strategy });
        this.factorModel = this.initFactorInput('factor-input', strategy);
      }

      if (!_.isUndefined(factor)) {
        // formatDecimal will ignore fraction by default
        const value = _.isNumber(factor) ? i18n.formatDecimal(
          factor,
          { minimumFractionDigits: 0, maximumFractionDigits: 1 }
        ) : factor;

        this.factorModel.set('Factor', value);
        this.getChild('factor-input').setState({ value });
        this.trigger('input-changed');
      }

      return this;
    }

    getFactor(editStrategy, factorModel) {
      const factor = factorModel.Factor;

      if (factor === null) {
        return _.noop();
      }
      let num = Number(factor) / 100;

      if (editStrategy === 'increase') {
        num += 1;
      } else {
        num = 1 - num;
      }
      // here we need to format decimal firstly to restrict the fraction from 1-3
      // mt only support fraction with 1-3
      // and we want to keep what user input instead of a fix fraction
      const formatDecimal =
        i18n.formatDecimal(num, { minimumFractionDigits: 1, maximumFractionDigits: 3 });
      // we need to parse decimal here since formatDecimal involve the locale information
      return i18n.parseDecimal(formatDecimal).toString();
    }

    initFactorInput(inputView, editStrategy) {
      const schema = multiplierSchema;

      if (editStrategy === 'increase') {
        _.extend(schema.properties.Factor, { minimum: 0, maximum: 1000 });
      } else {
        _.extend(schema.properties.Factor, { minimum: 0, maximum: 100 });
      }
      const FactorModel = backboneSchema(
        schema, i18n, ['Factor', '@odata.type'], null,
        {
          Factor: {
            validation: (validation) => {
              validation.decimalLength.decimalLength = 1; // eslint-disable-line
              return validation;
            },
          },
        }
      );

      const factorModel = new FactorModel();

      factorModel.set('Factor', '');

      const factorInput = new InputControl({
        entity: factorModel,
        field: 'Factor',
        addonText: pageContext.DecimalFormatter.percentSymbol,
        ariaLabel: i18n.getString('HotelCenter_Overview_Multipliers_Bids_Factor_Label'),
      });

      this.setChild(inputView, factorInput);

      return factorModel;
    }
  }

  return {
    BidMultiplierView,
  };
};
